'use client';

import React, { createContext, useContext, ReactNode, ReactElement } from 'react';
import { useAtfGlobalDialog } from './AtfGlobalDialogContext';
import { useRouter } from 'next/navigation';
import { useTheme } from '@mui/material';

interface AtfApiErrorHandlerContextType {
    handleApiError: (error: any, message?: string, icon?: ReactElement) => void;
    getValidationErrors: (error: any) => { [key: string]: string[] } | null;
}

const AtfApiErrorHandlerContext = createContext<AtfApiErrorHandlerContextType | undefined>(undefined);

export const AtfApiErrorHandlerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { showErrorDialog } = useAtfGlobalDialog();
    const router = useRouter();
    const theme = useTheme();

    /**
     * Extract validation errors from API error response
     * Returns { fieldName: [error1, error2, ...] } or null if no validation errors
     */
    const getValidationErrors = (error: any): { [key: string]: string[] } | null => {
        if (error instanceof Error && error.cause) {
            const cause = error.cause as any;
            if (cause.jsonResponse?.errors) {
                return cause.jsonResponse.errors;
            }
        }
        return null;
    };

    const handleApiError = (error: any, message?: string, icon?: ReactElement) => {
        let errorMessage: string = message || '서버 에러가 발생했습니다.<br>기술지원팀에 문의해주세요.';
        let handleClickConfirm = () => {};
        let hasValidationErrors = false;

        if (typeof error === 'string') {
            errorMessage = error;
        } else if (error instanceof Error) {
            if (error.cause) {
                const cause = error.cause as { response: Response; jsonResponse: any };
                const statusCode = cause.response.status;
                const jsonResponse = cause.jsonResponse;

                switch (statusCode) {
                    case 401:
                        errorMessage = `로그인 정보가 만료되었습니다.<br>로그인 페이지로 이동합니다.`;

                        handleClickConfirm = () => {
                            const token = localStorage.getItem('access_token');

                            if (token) {
                                localStorage.removeItem('access_token');
                            }

                            const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
                            const redirectParam = currentPath ? `?redirect=${encodeURIComponent(currentPath)}` : '';
                            router.push(`/login${redirectParam}`);
                        };
                        break;

                    default:
                        if (jsonResponse) {
                            // Check for validation errors FIRST, regardless of message parameter
                            if (jsonResponse.errors && typeof jsonResponse.errors === 'object') {
                                const errorDetails = Object.entries(jsonResponse.errors)
                                    .flatMap(([field, errors]: [string, any]) => {
                                        const errorList = Array.isArray(errors) ? errors : [errors];
                                        return errorList;
                                    })
                                    .join('<br>');
                                if (errorDetails) {
                                    errorMessage = errorDetails;
                                    hasValidationErrors = true;
                                } else {
                                    errorMessage = jsonResponse.message || message || '서버 에러가 발생했습니다.<br>기술지원팀에 문의해주세요.';
                                }
                            } else {
                                // Use message from API if available, otherwise use provided message
                                errorMessage = jsonResponse.message || message || '서버 에러가 발생했습니다.<br>기술지원팀에 문의해주세요.';
                            }
                        } else if (!message) {
                            errorMessage = '서버 에러가 발생했습니다.<br>기술지원팀에 문의해주세요.';
                        }
                        break;
                }
            }
        }

        // Only append error.message if we don't have validation errors
        if (!hasValidationErrors && error.message && error.message !== errorMessage) {
            errorMessage += '<br><br>' + error.message;
        }

        showErrorDialog(errorMessage, {
            icon: icon ?? undefined,
            buttons: [
                {
                    variant: 'contained',
                    color: 'error',
                    text: '확인',
                    sx: { width: '100%', color: '#FFF' },
                    onClick: handleClickConfirm,
                },
            ],
        });
    };

    return (
        <AtfApiErrorHandlerContext.Provider value={{ handleApiError, getValidationErrors }}>
            {children}
        </AtfApiErrorHandlerContext.Provider>
    );
};

export const useAtfApiErrorHandler = () => {
    const context = useContext(AtfApiErrorHandlerContext);
    if (context === undefined) {
        throw new Error('useAtfApiErrorHandler must be used within an AtfApiErrorHandlerProvider');
    }
    return context;
};
